import { styled } from '@linaria/react'
import React, { ComponentProps, FC, ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { useNavigate } from 'react-router'
import { VoteButtonGroup } from '../components/VoteButtonGroup'
import type { BoxChoiceDto } from '../types/generated'
import { Rhytm } from './Rhytm'
import { TitleAndSubtitle } from './TitleAndSubtitle'
import TonIcon from '../svgr/TonIcon'
import PeopleIcon from '../svgr/People'
import { Typography } from './Typography'
import { colors } from '../styles/colors'
import { BoxImage } from '../components/BoxImage'
import ArrowRight from '../svgr/ArrowRight'
import { BoxStatusBar, TagType } from './BoxStatusBar'
import { faqPagePath } from '../pages/FaqPage'

const StatsComponent = ({
  isSingleBox,
  totalParticipants,
  totalVotes,
}: {
  isSingleBox?: boolean
  totalParticipants?: number
  totalVotes?: number
}) => {
  return (
    <StatsSection isSingleBox={isSingleBox}>
      <StatItem>
        <PeopleIcon />
        <Typography fontSize={12} font="roboto">
          {totalParticipants || 0}
        </Typography>
      </StatItem>

      <StatItem>
        <TonIcon />
        <Typography fontSize={12} font="roboto">
          {totalVotes || 0}
        </Typography>
      </StatItem>
    </StatsSection>
  )
}

export const BoxView: FC<{
  boxChoices: BoxChoiceDto[]
  boxId: number
  description?: string
  expiration: ReactNode
  imgSrc?: string
  isExpired: boolean
  isSingleBox?: boolean
  isUnderModeration?: boolean
  onBoxChoiceClick: (index: number) => void
  onRewardClaim?: () => void
  progressPercentage?: number
  title: ReactNode
  totalParticipants?: number
  totalVotes?: number
  tags: Record<TagType, boolean>
  winnerChoiceIndex: number | null
}> = ({
  boxChoices,
  boxId,
  description,
  expiration,
  imgSrc,
  isExpired,
  isSingleBox,
  isUnderModeration,
  onBoxChoiceClick,
  onRewardClaim,
  progressPercentage,
  title,
  totalParticipants,
  totalVotes,
  tags,
  winnerChoiceIndex,
}) => {
  const navigate = useNavigate()
  const hasUserVote = boxChoices.some(choice => !!choice.user_vote_summary)

  const getStatus = (): ComponentProps<typeof BoxStatusBar>['status'] => {
    return undefined
  }

  return (
    <BoxContainer>
      <BoxImage
        boxId={boxId}
        expiration={expiration}
        hasYourVote={hasUserVote}
        imgSrc={imgSrc}
        isExpired={isExpired}
        isUnderModeration={isUnderModeration}
        onRewardClaim={onRewardClaim}
        progressPercentage={progressPercentage}
        status={getStatus()}
        tags={tags}
      />

      <BoxContent isSingleBox={isSingleBox}>
        <MainInfoContainer isSingleBox={isSingleBox}>
          <TitleSection isSingleBox={isSingleBox}>
            <TitleAndSubtitle
              title={title}
              subtitle={isSingleBox ? description : undefined}
              titleFontSize={isSingleBox ? 20 : 16}
              isShowSeparator={false}
            />
          </TitleSection>

          <StatsSection isSingleBox={isSingleBox}>
            <StatsComponent
              totalParticipants={totalParticipants}
              totalVotes={totalVotes}
              isSingleBox={isSingleBox}
            />

            {isSingleBox && (
              <HowItWorksButton onClick={() => navigate(`${faqPagePath}?section=whatIsTheBox`)}>
                <Typography fontSize={12} fontWeight={600} font="roboto">
                  <FormattedMessage
                    id="box.howItWorks"
                    defaultMessage="Как это работает?"
                  />
                </Typography>
                <ArrowRight />
              </HowItWorksButton>
            )}
          </StatsSection>
        </MainInfoContainer>

        <VoteButtonGroup
          boxChoices={boxChoices}
          onSelect={onBoxChoiceClick}
          isOpen={!isExpired}
          isProfitabilityShow={isSingleBox}
          winnerChoiceIndex={winnerChoiceIndex}
        />
      </BoxContent>
    </BoxContainer>
  )
}

const StatsSection = styled.div<{ isSingleBox?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0;
`

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 12px;
    height: 12px;
    color: ${colors.textSecondary};
  }
`
const BoxContent = styled(Rhytm)<{ isSingleBox?: boolean }>`
  padding: 16px;
  gap: 12px;
`

const BoxContainer = styled.div`
  background: ${colors.white};
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid ${colors.grey200};
`

const HowItWorksButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${colors.textSecondary};

  svg {
    width: 12px;
    height: 12px;
  }
`
const MainInfoContainer = styled.div<{ isSingleBox?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const TitleSection = styled.div<{ isSingleBox?: boolean }>`
  order: ${({ isSingleBox }) => (isSingleBox ? 1 : 2)};
`
